import React, { useCallback, useEffect, useState } from "react";

import logger from "../../../../../utils/LoggerFrontend.js";
import CampaignCreateTeaserEditor from "../../Campaign__Create_TeaserEditor/Campaign__Create_TeaserEditor.jsx";
import ChannelDataEditor from "../../Campaign__Create_ChannelDataEditor.jsx";
import {
  SearchIcon,
  AddIcon,
  HearthIcon,
  HomeIcon,
  MessageIcon,
  PersonIcon,
  ReplyIcon,
  TiktokInboxIcon,
  NoteIcon
} from "../../Svg/index.js";
import { channelsByShortName } from "../../../../../data/app__channels.js";
import * as SC from "./PostEditorTikTok.styled.js";
import { Switch } from "../../Components/index.js";
import { WebIcon, MobileIcon } from "../../Svg/index.js";

import TemplatePostActions from "../../TemplatePost/TemplatePostActions.js";
import TemplatePostPreview from "../../TemplatePost/TemplatePost__Preview/TemplatePostPreview.jsx";
import CampaignCreateVideoEditor from "../../campaign-create-video-editor/CampaignCreateVideoEditor.jsx";
import { Floating } from "@talentbait/shared-components";
import Select from "react-select";
import { DEFAULT_JINGLES } from "../../VideoGenerator/VideoGenerator__Editor/VideoGenerator__Editor.constants.js";
import { useTranslation } from "react-i18next";
import Actions from "../../../../../Actions.js";

const rearrange = (arr) => [arr.at(-1), ...arr.slice(0, -1)];

const AUDIO_OPTIONS = rearrange(DEFAULT_JINGLES);

const getAudioLabel = (audioUrl) => {
  const audio = AUDIO_OPTIONS.find((jingle) => jingle.value === audioUrl);
  return audio ? audio : "No Sound";
};

const PostEditorTikTok = ({
  orgaId,
  postData,
  channelData,
  updatePost,
  updateChannelData,
  editable,
  channel,
  selectedCreativeIndexToEdit,
  clientView,
  campaignLanguage
}) => {
  const { t } = useTranslation();

  const [isVideoGeneratorOpen, setIsVideoGeneratorOpen] = useState(false);
  const [musicOpen, setMusicOpen] = useState(false);
  const [showAnimation, setShowAnimation] = useState(true);
  const [previewType, setPreviewType] = useState(
    postData.videoData?.isGenerated ? "video" : "normal"
  );

  const saveVideoData = useCallback(
    (updates) => {
      updatePost({ videoData: { ...postData.videoData, ...updates } });
    },
    [updatePost, postData.videoData]
  );

  useEffect(() => {
    if (postData.videoData?.audioUrl === undefined) {
      saveVideoData({ audioUrl: "" });
    }
  }, [postData.videoData?.audioUrl, saveVideoData]);

  useEffect(() => {
    if (previewType === "video") {
      Actions.updateTemplateDataToDownload(null);
      TemplatePostActions.updateTemplateType({ templateType: "video", isTikTokVideo: true });
    } else {
      TemplatePostActions.updateTemplateType({ templateType: "image" });
    }
  }, [previewType]);

  const handlePreviewType = useCallback(() => {
    const type = previewType === "normal" ? "video" : "normal";

    if (window.analytics) {
      window.analytics.track("Switch Preview Clicked", {
        source: "Post Editor TikTok",
        type,
        campaignId: postData.campaignId
      });
    }

    setPreviewType(type);
    if (type === "video") {
      saveVideoData({ isGenerated: type === "video", templateId: "TikTokVideo" });
    }
  }, [postData, saveVideoData, previewType]);

  const togglePreviewEditor = () => {
    if (showAnimation) setShowAnimation(false);
    setIsVideoGeneratorOpen(!isVideoGeneratorOpen);
  };

  const toggleMusicEditor = () => {
    if (showAnimation) setShowAnimation(false);
    setMusicOpen(!musicOpen);
  };

  const updateVideoData = useCallback(
    (videoData) => {
      logger.debug("PostEditorTikTok:updateVideoData", "useCallback:", {
        videoData
      });

      setIsVideoGeneratorOpen(false);
      updatePost({ ["videoData"]: { ...postData.videoData, ...videoData } });
    },
    [updatePost, postData.videoData]
  );

  const header = (
    <SC.HeaderWrapper>
      <SC.MobileHead>09:41</SC.MobileHead>
      <SC.HeaderMenu>
        {t("Following", { lng: campaignLanguage })} |{" "}
        <SC.SelectedMenuItem>{t("For You", { lng: campaignLanguage })}</SC.SelectedMenuItem>
      </SC.HeaderMenu>
    </SC.HeaderWrapper>
  );

  const rightMenu = (
    <SC.RightMenu>
      {previewType === "normal" ? (
        <Floating
          fill="white"
          placement="right"
          open={musicOpen}
          onOpenChange={setMusicOpen}
          trigger={
            <SC.MenuItem>
              <SC.Music showAnimation={showAnimation} onClick={toggleMusicEditor} />
            </SC.MenuItem>
          }
          content={
            <SC.MusicPicker>
              <SC.MusicLabel>
                <label htmlFor="tiktokAudioUrl">{t("Update Video Audio")}</label>
                <Select
                  id="tiktokAudioUrl"
                  options={AUDIO_OPTIONS}
                  onChange={(audio) => saveVideoData({ audioUrl: audio.value })}
                  value={getAudioLabel(postData?.videoData?.audioUrl)}
                  placeholder={t("Music")}
                  styles={SC.customMusicStyles}
                />
              </SC.MusicLabel>
            </SC.MusicPicker>
          }
        />
      ) : (
        <SC.MenuItem>
          <SC.Edit showAnimation={showAnimation} onClick={togglePreviewEditor} />
        </SC.MenuItem>
      )}
      <SC.MenuItem>
        <HearthIcon />
        <div>328.7K</div>
      </SC.MenuItem>
      <SC.MenuItem>
        <MessageIcon />
        <div>578</div>
      </SC.MenuItem>
      <SC.MenuItem>
        <ReplyIcon style={{ transform: "scale(-1, 1)" }} />
        <div>{t("Share", { lng: campaignLanguage })}</div>
      </SC.MenuItem>
    </SC.RightMenu>
  );

  const footer = (
    <SC.FooterWrapper>
      <SC.MenuItem>
        <HomeIcon />
        <div>{t("Home", { lng: campaignLanguage })}</div>
      </SC.MenuItem>
      <SC.MenuItem>
        <SearchIcon style={{ margin: "4px -5px -4px 0px" }} />
        <div>{t("Discovery", { lng: campaignLanguage })}</div>
      </SC.MenuItem>
      <SC.Add>
        <AddIcon />
      </SC.Add>
      <SC.MenuItem>
        <TiktokInboxIcon />
        <div>{t("Inbox", { lng: campaignLanguage })}</div>
      </SC.MenuItem>
      <SC.MenuItem>
        <PersonIcon />
        <div>{t("Me", { lng: campaignLanguage })}</div>
      </SC.MenuItem>
      <SC.WhiteLine />
    </SC.FooterWrapper>
  );

  const templatePreviewSizes = {
    minHeight: SC.TIKTOK_IMAGE_SIZE_SPEC.minHeight,
    maxHeight: SC.TIKTOK_IMAGE_SIZE_SPEC.maxHeight,
    width: SC.TIKTOK_IMAGE_SIZE_SPEC.pictureWidth,
    minAspect: channelsByShortName.tiktok.minAspect,
    maxAspect: channelsByShortName.tiktok.maxAspect
  };

  logger.debug("PostEditorTikTok:render", "render:", {
    postData,
    channelData,
    updatePost,
    editable,
    clientView
  });

  return (
    <SC.PreviewRender>
      <SC.PreviewModeSwitchWrapper>
        <Switch
          isLeft={previewType === "normal"}
          switchAction={handlePreviewType}
          leftOption={{
            Icon: WebIcon,
            isStroke: true,
            label: "Upload media"
          }}
          rightOption={{
            Icon: MobileIcon,
            isStroke: true,
            label: "Generate video"
          }}
        />
      </SC.PreviewModeSwitchWrapper>

      <SC.TikTokWrapper
        width={SC.TIKTOK_IMAGE_SIZE_SPEC.width}
        height={SC.TIKTOK_IMAGE_SIZE_SPEC.height}
      >
        <SC.PhotoWrapper maxHeight={SC.TIKTOK_IMAGE_SIZE_SPEC.maxHeight}>
          {header}

          {previewType === "normal" ? (
            <CampaignCreateVideoEditor
              orgaId={orgaId}
              selectedCreativeIndexToEdit={selectedCreativeIndexToEdit}
              creatives={postData.creatives}
              updatePost={updatePost}
              minHeight={SC.TIKTOK_IMAGE_SIZE_SPEC.minHeight}
              maxHeight={SC.TIKTOK_IMAGE_SIZE_SPEC.maxHeight}
              minAspect={channelsByShortName.tiktok.minAspect}
              maxAspect={channelsByShortName.tiktok.maxAspect}
              width={SC.TIKTOK_IMAGE_SIZE_SPEC.pictureWidth}
              editable={editable}
              allowVideos={channelsByShortName.tiktok.allowVideos}
              videoData={postData?.videoData}
            />
          ) : (
            <TemplatePostPreview
              orgaId={orgaId}
              creatives={postData.creatives}
              creativeAssets={postData.creativeAssets}
              videoData={postData.videoData}
              updatePost={updateVideoData}
              isVideoGeneratorOpen={isVideoGeneratorOpen}
              channel={channel}
              templatePreviewSizes={templatePreviewSizes}
            />
          )}
          <SC.EditorWrapper>
            <SC.CompanyName>
              @
              <ChannelDataEditor
                updateChannelData={updateChannelData}
                value={channelData.companyName}
                dataKey="companyName"
                textStyle={SC.companyNameStyle}
                limit={channelsByShortName.tiktok.limits.companyName}
                label="Firmenname"
                tooltip="Firmenname"
                editable={editable}
                padding="3px 10px 3px 1px"
                dark
              />
            </SC.CompanyName>
            <CampaignCreateTeaserEditor
              channel="tiktok"
              updatePost={updatePost}
              teaserText={postData.teaserText}
              teaserTextStyle={SC.teaserTextStyle}
              smallButtons
              minRows={1}
              editable={editable}
              limit={channelsByShortName.tiktok.limits.teaserText}
              bannedCharaters={["#"]}
              dark
              isAutoGenerated={postData?.isAutoGenerated}
              isEdited={postData?.editedStates?.teaserText}
            />
            <SC.PromotionalText>
              <NoteIcon />
              {t("Promotional Music", { lng: campaignLanguage })}
            </SC.PromotionalText>
          </SC.EditorWrapper>
          {rightMenu}
        </SC.PhotoWrapper>
        {footer}
      </SC.TikTokWrapper>
    </SC.PreviewRender>
  );
};

export default PostEditorTikTok;
